import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  FaUserMd, 
  FaGraduationCap, 
  FaChalkboardTeacher,
  FaClipboardList,
  FaChartLine,
  FaBook,
  FaCertificate,
  FaFileAlt,
  FaSpinner,
  FaCog,
  FaHospitalUser,
  FaBriefcaseMedical,
  FaHandHoldingMedical,
  FaNotesMedical,
  FaInfoCircle,
  FaExclamationTriangle,
  // Add missing icons
  FaDesktop,
  FaFile,
  FaRegLightbulb,
  FaUsers,
  FaLaptopMedical,
  FaMedal,
  FaAward
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { listUserProfiles } from '../../../graphql/queries';
import { fetchAllPathfinderIMTDomainScores } from '../../../api/pathfinderIMTDomainScoreApi';
import { fetchAllPathfinderCSTDomainScores } from '../../../api/pathfinderCSTDomainScoreApi';
import { fetchAllPathfinderACCSDomainScores } from '../../../api/pathfinderACCSDomainScoreApi';
import { fetchAllPathfinderNeuroSurgDomainScores } from '../../../api/pathfinderNeuroSurgDomainScoreApi';
import { fetchAllPathfinderRadiologyDomainScores } from '../../../api/pathfinderRadiologyDomainScoreApi';

// Map of icon names to components
const iconComponentMap = {
  "Postgraduate Degrees": FaGraduationCap,
  "Presentations/Posters": FaDesktop,
  "Publications": FaFile,
  "Quality Improvement": FaRegLightbulb,
  "Teaching Experience": FaChalkboardTeacher,
  "Training in Teaching": FaUsers,
  "MRCS": FaBook,
  "Other Qualifications": FaCertificate,
  "Surgical Experience": FaBriefcaseMedical,
  "Surgical Skills": FaLaptopMedical,
  "Audit and Quality Improvement": FaClipboardList,
  "Research": FaChartLine,
  "Teaching": FaChalkboardTeacher,
  "Leadership and Management": FaUserMd,
  "Academia": FaGraduationCap,
  "Additional Achievements": FaMedal,
  "default": FaAward
};

const iconMapIMT = {
  category1: FaUserMd,
  category2: FaGraduationCap,
  category3: FaChalkboardTeacher,
  category4: FaBook,
  category5: FaChartLine,
  category6: FaClipboardList,
  category7: FaCertificate,
};

const iconMapCST = {
  category1: FaCog || FaUserMd, // Fallback if FaScalpel doesn't exist
  category2: FaGraduationCap,
  category3: FaHandHoldingMedical || FaChalkboardTeacher, // Fallback
  category4: FaNotesMedical || FaBook, // Fallback
  category5: FaChartLine,
  category6: FaBriefcaseMedical || FaClipboardList, // Fallback
  category7: FaHospitalUser || FaCertificate, // Fallback
};

// Add ACCS icon map
const iconMapACCS = {
  category1: FaUserMd,
  category2: FaGraduationCap,
  category3: FaChalkboardTeacher,
  category4: FaBook,
  category5: FaChartLine,
  category6: FaClipboardList,
  category7: FaCertificate,
};

// Add Neurosurgery icon map (mapping generic categories to icons)
const iconMapNeurosurgery = {
  category1: FaGraduationCap, // Undergrad
  category2: FaHospitalUser,  // Electives
  category3: FaGraduationCap, // Higher Degree
  category4: FaGraduationCap, // First Degree
  category5: FaCertificate,   // Other Degrees
  category6: FaChartLine,     // PG Research
  category7: FaMedal,         // PG Awards
  category8: FaBook,          // PG Courses
  category9: FaFile,          // Publications
  category10: FaDesktop,      // Presentations
  category11: FaClipboardList,// Audit/QI
  category12: FaUserMd,       // Leadership
  category13: FaBriefcaseMedical, // Operative
  category14: FaChalkboardTeacher, // Teaching
  category15: FaFileAlt,      // Personal Statement
  category16: FaHospitalUser, // Placement
};

const iconMapRadiology = {
  category1: FaHandHoldingMedical, // Commitment
  category2: FaUserMd,             // Leadership
  category3: FaChalkboardTeacher,  // Teaching
  category4: FaClipboardList,      // Audit/QI
  category5: FaGraduationCap,      // Academic
};

const colorMap = {
  category1: '#4299E1',
  category2: '#48BB78',
  category3: '#ED8936',
  category4: '#9F7AEA',
  category5: '#F56565',
  category6: '#38B2AC',
  category7: '#667EEA',
};

// Remove all mock data completely
const mockDomainData = {
  // Remove all mock data - we only want real backend data
};

const MiddleView = ({ trainingPath = 'IMT' }) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [categories, setCategories] = useState([]);
  const [currentPoints, setCurrentPoints] = useState({});
  const [showThresholdInfo, setShowThresholdInfo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  const notificationTimeout = useRef(null);
  const [userProfile, setUserProfile] = useState(null);

  // Function to display notifications
  const showNotification = (message, type = 'success') => {
    if (notificationTimeout.current) {
      clearTimeout(notificationTimeout.current);
    }
    
    setNotification({
      show: true,
      message,
      type
    });
    
    notificationTimeout.current = setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // Load domain points from backend when trainingPath changes
  useEffect(() => {
    const loadDomainPoints = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let domainScores = [];
        
        // Fetch real data from backend based on training path
        if (trainingPath === 'IMT') {
          domainScores = await fetchAllPathfinderIMTDomainScores();
        } else if (trainingPath === 'CST') {
          domainScores = await fetchAllPathfinderCSTDomainScores();
        } else if (trainingPath === 'ACCS') {
          domainScores = await fetchAllPathfinderACCSDomainScores();
        } else if (trainingPath === 'Neurosurgery') {
          domainScores = await fetchAllPathfinderNeuroSurgDomainScores();
        } else if (trainingPath === 'Radiology') {
          domainScores = await fetchAllPathfinderRadiologyDomainScores();
        }
        
        console.log(`Loaded ${trainingPath} domain scores:`, domainScores);
        
        if (domainScores && domainScores.length > 0) {
          // Transform domain scores into the categories format
          const transformedCategories = domainScores.map((score, index) => {
            // Calculate total points based on training path and domain
            let totalPoints = 100; // Default
            
            // Set realistic total points based on actual pathway data
            if (trainingPath === 'IMT') {
              const imtTotals = {
                
                'Postgraduate Degrees': 4,
                'Presentations/Posters': 6,
                'Publications': 8,
                'Quality Improvement': 4,
                'Teaching Experience': 5,
                'Training in Teaching': 3
              };
              totalPoints = imtTotals[score.domainKey] || 8;
            } else if (trainingPath === 'CST') {
              const cstTotals = {
                'Operative Experience': 8,
                'Surgical Experience': 4,
                'Quality Improvement/Audit': 8,
                'Presentations/Publications': 8,
                'Teaching Experience': 8
              };
              totalPoints = cstTotals[score.domainKey] || 8;
            } else if (trainingPath === 'ACCS') {
              const accsTotals = {
                'MSRA: Clinical Problem Solving': 14,
                'MSRA: Professional Dilemmas': 14,
                'Interview: Commitment to Specialty': 10,
                'Interview: Teamwork & Stress': 10,
                'Interview: Teaching & QI': 10,
                'Interview: EM Knowledge': 10,
                'Interview: Clinical Scenarios': 10
              };
              totalPoints = accsTotals[score.domainKey] || 10;
            } else if (trainingPath === 'Neurosurgery') {
              const neuroTotals = {
                "Undergraduate Career (research, audit, QI, prizes/awards)": 10,
                "Undergraduate clinical electives and attachments": 3,
                "Higher Degree (full-time original research e.g. PhD/MD/Masters by research; excluding degrees integral to curriculum)": 4,
                "First Degree in Medical Sciences (e.g. BSc/BMedSci/MRes; excluding intercalated degrees as part of medical course)": 3,
                "Other Degrees and Diplomas": 2,
                "Postgraduate Research": 5,
                "Postgraduate Academic Awards and Prizes": 4,
                "Postgraduate Clinical Courses (including neurosurgery-relevant and surgical skills courses)": 4,
                "Publications (accepted for publication as a minimum standard)": 18,
                "Presentations": 8,
                "Clinical Audit and Quality Improvement Projects": 8,
                "Organisational and Leadership Skills (including management experience and understanding of NHS management/resources)": 4,
                "Operative Experience (including validated logbook documentation of surgical exposure)": 15,
                "Teaching (including experience of teaching and communication to a range of audiences)": 10,
                "Personal Statement 1 (demonstrating insight, commitment, understanding of programme, and NHS values)": 0,
                "Surgical Placement Experience in Neurosurgery and Allied Specialties": 15
              };
              totalPoints = neuroTotals[score.domainKey] || 10;
            } else if (trainingPath === 'Radiology') {
              const radiologyTotals = {
                "Commitment to Specialty (double-weighted domain)": 8,
                "Leadership and Management (minimum role duration 6 months)": 4,
                "Teaching and Training": 8,
                "Audit and Quality Improvement": 8,
                "Academic Achievements (postgraduate achievements only for scoring)": 8
              };
              totalPoints = radiologyTotals[score.domainKey] || 8;
            }
            
            return {
              label: score.domainKey,
              categoryKey: score.domainKey,
              progress: Math.round((score.progress / totalPoints) * 100) || 0,
              currentPoints: score.progress || 0,
              totalPoints: totalPoints,
              color: colorMap[`category${index + 1}`] || colorMap.category1,
              icon: getIconForDomain(score.domainKey, trainingPath, index)
            };
          });
          
          setCategories(transformedCategories);
          
          // Extract current points to a separate object for easier access
          const pointsMap = {};
          transformedCategories.forEach(category => {
            pointsMap[category.categoryKey] = category.currentPoints || 0;
          });
          
          setCurrentPoints(pointsMap);
          console.log('Loaded domain points:', transformedCategories);
          
          // Remove success popup when data loads
          // showNotification(`${trainingPath} progress loaded successfully!`, 'success');
        } else {
          // No mock data - show empty state
          setError(`No ${trainingPath} progress data available. Please configure your pathway first.`);
          setCategories([]);
          setCurrentPoints({});
        }
      } catch (err) {
        console.error('Error loading domain points:', err);
        setError(`Failed to load ${trainingPath} progress: ${err.message}`);
        setCategories([]);
        setCurrentPoints({});
      } finally {
        setLoading(false);
      }
    };
    
    loadDomainPoints();
    
    // Cleanup notification timeout
    return () => {
      if (notificationTimeout.current) {
        clearTimeout(notificationTimeout.current);
      }
    };
  }, [trainingPath]);
  
  // Helper function to get the appropriate icon for each domain
  const getIconForDomain = (domainKey, pathway, index) => {
    // Try to get icon by name from iconComponentMap
    if (iconComponentMap[domainKey]) {
      return iconComponentMap[domainKey];
    }
    
    // Fall back to pathway-specific icons by index
    if (pathway === 'IMT' && iconMapIMT[`category${index + 1}`]) {
      return iconMapIMT[`category${index + 1}`];
    } else if (pathway === 'CST' && iconMapCST[`category${index + 1}`]) {
      return iconMapCST[`category${index + 1}`];
    } else if (pathway === 'ACCS' && iconMapACCS[`category${index + 1}`]) {
      return iconMapACCS[`category${index + 1}`];
    } else if (pathway === 'Neurosurgery' && iconMapNeurosurgery[`category${index + 1}`]) {
      return iconMapNeurosurgery[`category${index + 1}`];
    } else if (pathway === 'Radiology' && iconMapRadiology[`category${index + 1}`]) {
      return iconMapRadiology[`category${index + 1}`];
    }
    
    // Default icon if nothing else matches
    return iconComponentMap.default;
  };
  
  // Calculate the threshold based on second highest score
  const threshold = useMemo(() => {
    if (categories.length === 0) return 0;
    
    // Get all the progress percentages
    const progressValues = categories.map(category => category.progress);
    
    // If we have fewer than 2 categories, use the highest value
    if (progressValues.length < 2) return Math.max(...progressValues, 0);
    
    // Sort in descending order and get the second highest
    const sortedProgress = [...progressValues].sort((a, b) => b - a);
    return sortedProgress[1]; // Second highest value
  }, [categories]);

  // Function to determine if a category is above threshold
  const isAboveThreshold = (progress) => {
    return progress >= threshold;
  };

  // Fetch user profile when component mounts
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const user = await getCurrentUser();
        if (!user) {
          console.error('No authenticated user found');
          return;
        }
        
        const client = generateClient();
        const { data } = await client.graphql({
          query: listUserProfiles,
          variables: {
            filter: { owner: { eq: user.username } }
          },
          authMode: 'userPool'
        });
        
        if (data.listUserProfiles.items.length > 0) {
          setUserProfile(data.listUserProfiles.items[0]);
          console.log('User profile loaded:', data.listUserProfiles.items[0]);
        } else {
          console.log('No user profile found');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        showNotification('Failed to load user profile data', 'error');
      }
    };
    
    fetchUserProfile();
  }, []);

  const handleGenerateCV = async () => {
    try {
      setIsGenerating(true);
      setLoadingProgress(0);
      
      // Check if we have user profile data
      if (!userProfile) {
        // Try to fetch it if not already loaded
        try {
          const user = await getCurrentUser();
          if (!user) {
            throw new Error('User not authenticated');
          }
          
          const client = generateClient();
          const { data } = await client.graphql({
            query: listUserProfiles,
            variables: {
              filter: { owner: { eq: user.username } }
            },
            authMode: 'userPool'
          });
          
          if (data.listUserProfiles.items.length > 0) {
            setUserProfile(data.listUserProfiles.items[0]);
          } else {
            throw new Error('No user profile found. Please complete your profile first.');
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          showNotification('Failed to load user profile. Please try again.', 'error');
          setIsGenerating(false);
          return;
        }
      }
      
      // Prepare the CV data from real sources
      const cvData = {
        userProfile: userProfile,
        domainScores: categories.map(category => ({
          domainKey: category.categoryKey,
          progress: category.currentPoints,
          totalPoints: category.totalPoints,
          percentage: category.progress,
          color: category.color
        })),
        trainingPath: trainingPath,
        totalPoints: categories.reduce((sum, cat) => sum + cat.currentPoints, 0),
        totalMaxPoints: categories.reduce((sum, cat) => sum + cat.totalPoints, 0),
        overallPercentage: Math.round((categories.reduce((sum, cat) => sum + cat.currentPoints, 0) / 
                             categories.reduce((sum, cat) => sum + cat.totalPoints, 0)) * 100) || 0
      };
      
      console.log('Generating CV with real data:', cvData);
      
      // Show progress animation
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              setIsGenerating(false);
              navigate('/cv-gen', { state: { cvData } });
            }, 500);
            return 100;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
      
    } catch (error) {
      console.error('Error preparing CV data:', error);
      showNotification(`Failed to generate CV: ${error.message}`, 'error');
      setIsGenerating(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="mv-loading">
        <div className="mv-spinner"></div>
        <p>Loading your {trainingPath} progress data...</p>
        
        <style jsx>{`
          .mv-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            text-align: center;
            padding: 2rem;
          }
          .mv-spinner {
            border: 4px solid rgba(0, 0, 0, 0.1);
            border-left-color: #3b82f6;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  // Show error state if there was an error
  if (error) {
    return (
      <div className="mv-error">
        <FaExclamationTriangle className="mv-error-icon" />
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
        
        <style jsx>{`
          .mv-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            text-align: center;
            padding: 2rem;
          }
          .mv-error-icon {
            color: #ef4444;
            font-size: 2rem;
            margin-bottom: 1rem;
          }
          .mv-error p {
            color: #ef4444;
            margin-bottom: 1.5rem;
            max-width: 500px;
          }
          .mv-error button {
            background-color: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.375rem;
            padding: 0.5rem 1rem;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          .mv-error button:hover {
            background-color: #2563eb;
          }
        `}</style>
      </div>
    );
  }

  // No data to display
  if (categories.length === 0) {
    return (
      <div className="mv-no-data">
        <FaInfoCircle className="mv-no-data-icon" />
        <p>No {trainingPath} progress data available.</p>
        <button onClick={() => window.location.reload()}>Refresh</button>
        
        <style jsx>{`
          .mv-no-data {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            text-align: center;
            padding: 2rem;
          }
          .mv-no-data-icon {
            color: #3b82f6;
            font-size: 2rem;
            margin-bottom: 1rem;
          }
          .mv-no-data p {
            color: #64748b;
            margin-bottom: 1.5rem;
          }
          .mv-no-data button {
            background-color: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.375rem;
            padding: 0.5rem 1rem;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          .mv-no-data button:hover {
            background-color: #2563eb;
          }
        `}</style>
      </div>
    );
  }

  // Calculate total points and percentage
  const totalPoints = categories.reduce((sum, cat) => sum + cat.currentPoints, 0);
  const totalMaxPoints = categories.reduce((sum, cat) => sum + cat.totalPoints, 0);
  const overallPercentage = Math.round((totalPoints / totalMaxPoints) * 100) || 0;
  
  return (
    <div className="mv-info-box mv-middle-view">
      {/* Notification popup */}
      {notification.show && (
        <div className={`mv-notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button 
            className="mv-notification-close" 
            onClick={() => setNotification(prev => ({ ...prev, show: false }))}
          >
            ×
          </button>
        </div>
      )}
      
      <h3 className="mv-middle-view-title">
        {trainingPath} Training Progress
      </h3>
      <div className="mv-training-path-badge">
        {trainingPath} Pathway
      </div>
      
      <div className="mv-threshold-indicator">
        <div className="mv-threshold-line-container">
          <div className="mv-threshold-label" onClick={() => setShowThresholdInfo(!showThresholdInfo)}>
            <FaInfoCircle className="mv-info-icon" />
            <span>Interview Threshold</span>
          </div>
          <div className="mv-threshold-line"></div>
        </div>
        {showThresholdInfo && (
          <div className="mv-threshold-tooltip">
            <p>The threshold represents the second highest domain score in your profile. Domains above this threshold are more likely to be positively evaluated in interviews.</p>
          </div>
        )}
      </div>
      
      <div className="mv-gantt-chart">
        {categories.map(({ label, categoryKey, progress, currentPoints, totalPoints, icon: Icon, color }, idx) => {
          const aboveThreshold = isAboveThreshold(progress);
          
          return (
            <div key={idx} className={`mv-progress-row ${aboveThreshold ? 'mv-above-threshold' : 'mv-below-threshold'}`}>
              <div className="mv-label-section">
                <div className={`mv-category-icon-wrapper ${aboveThreshold ? 'mv-icon-above' : 'mv-icon-below'}`}>
                  <Icon className="mv-category-icon" />
                </div>
                <span className="mv-task-label">{label}</span>
              </div>
              <div className="mv-progress-section">
                <div className="mv-progress-bar-bg">
                  <div 
                    className={`mv-progress-bar-fill ${aboveThreshold ? 'mv-fill-above' : 'mv-fill-below'}`}
                    style={{ width: `${progress}%`, backgroundColor: aboveThreshold ? color : '#94a3b8' }}
                  />
                  <div 
                    className="mv-threshold-marker"
                    style={{ left: `${threshold}%` }}
                  />
                </div>
                <span className={`mv-progress-text ${aboveThreshold ? 'mv-text-above' : 'mv-text-below'}`}>
                  {currentPoints}/{totalPoints} pts
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      <button 
        className={`mv-cv-generate-button ${isGenerating ? 'mv-generating' : ''}`}
        onClick={handleGenerateCV}
        disabled={isGenerating}
      >
        {isGenerating ? (
          <>
            <FaSpinner className="mv-button-icon mv-spinning" />
            Generating CV...
          </>
        ) : (
          <>
            <FaFileAlt className="mv-button-icon" />
            Generate Current CV
          </>
        )}
      </button>

      {isGenerating && (
        <div className="mv-loading-container">
          <div className="mv-loading-bar-wrapper">
            <div 
              className="mv-loading-bar" 
              style={{ width: `${Math.min(loadingProgress, 100)}%` }}
            />
          </div>
          <div className="mv-loading-text">
            {Math.min(Math.round(loadingProgress), 100)}%
          </div>
        </div>
      )}

      <style jsx>{`
        /* Rename this class to avoid conflicts with the global .info-box */
        .mv-info-box {
          background-color: #fff;
          border: 1px solid #ccc;
          border-radius: 8px;
          padding: 15px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          position: relative;
          transition: all 0.3s ease;
        }
        
        .mv-middle-view {
          width: 100%;
          margin: 0 auto;
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .mv-middle-view-title {
          text-align: center;
          margin-bottom: 1rem;
          font-size: 1.5rem;
          color: #2D3748;
        }
        
        .mv-training-path-badge {
          display: inline-block;
          margin: 0 auto 1.5rem;
          padding: 0.5rem 1rem;
          background: ${trainingPath === 'IMT' ? '#ebf8ff' : trainingPath === 'CST' ? '#f0fff4' : trainingPath === 'ACCS' ? '#fff4e5' : trainingPath === 'Neurosurgery' ? '#f3e8ff' : '#e6fffa'};
          color: ${trainingPath === 'IMT' ? '#3182ce' : trainingPath === 'CST' ? '#38a169' : trainingPath === 'ACCS' ? '#e53e3e' : trainingPath === 'Neurosurgery' ? '#805ad5' : '#319795'};
          border: 1px solid ${trainingPath === 'IMT' ? '#bee3f8' : trainingPath === 'CST' ? '#c6f6d5' : trainingPath === 'ACCS' ? '#fed7aa' : trainingPath === 'Neurosurgery' ? '#d8b4fe' : '#b2f5ea'};
          border-radius: 2rem;
          font-size: 0.875rem;
          font-weight: 500;
          text-align: center;
          display: block;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }
        
        .mv-threshold-indicator {
          position: relative;
          margin-bottom: 1.5rem;
        }
        
        .mv-threshold-line-container {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }
        
        .mv-threshold-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: #64748b;
          font-weight: 500;
          padding: 0.25rem 0.75rem;
          background: #f8fafc;
          border-radius: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .mv-threshold-label:hover {
          background: #f1f5f9;
          color: #475569;
        }
        
        .mv-info-icon {
          color: #3b82f6;
          font-size: 0.875rem;
        }
        
        .mv-threshold-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(to right, 
            rgba(203, 213, 225, 0.3), 
            rgba(203, 213, 225, 0.8) 50%, 
            rgba(203, 213, 225, 0.3)
          );
        }
        
        .mv-threshold-tooltip {
          position: absolute;
          left: 0;
          width: 100%;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          color: #64748b;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          animation: mv-fadeIn 0.2s ease;
          z-index: 10;
        }
        
        .mv-threshold-tooltip p {
          margin: 0;
          line-height: 1.5;
        }
        
        @keyframes mv-fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mv-gantt-chart {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .mv-progress-row {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          transition: all 0.2s ease;
        }
        
        .mv-progress-row:hover {
          background: #f8fafc;
        }
        
        .mv-above-threshold {
          border-left: 3px solid #10b981;
        }
        
        .mv-below-threshold {
          border-left: 3px solid #94a3b8;
        }

        .mv-label-section {
          width: 200px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .mv-category-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 2.25rem;
          border-radius: 50%;
          transition: all 0.2s ease;
        }
        
        .mv-icon-above {
          background: #ecfdf5;
          color: #10b981;
        }
        
        .mv-icon-below {
          background: #f1f5f9;
          color: #64748b;
        }

        .mv-category-icon {
          font-size: 1.25rem;
        }

        .mv-task-label {
          font-size: 0.9rem;
          color: #4A5568;
          font-weight: 500;
        }

        .mv-progress-section {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
        }

        .mv-progress-bar-bg {
          flex: 1;
          height: 8px;
          background-color: #EDF2F7;
          border-radius: 4px;
          overflow: hidden;
          position: relative;
        }

        .mv-progress-bar-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        
        .mv-fill-above {
          background: linear-gradient(90deg, rgba(16,185,129,0.7) 0%, rgba(16,185,129,1) 100%);
        }
        
        .mv-fill-below {
          background: linear-gradient(90deg, rgba(148,163,184,0.7) 0%, rgba(148,163,184,1) 100%);
        }
        
        .mv-threshold-marker {
          position: absolute;
          top: -4px;
          width: 2px;
          height: 16px;
          background: #3b82f6;
          border-radius: 1px;
          transform: translateX(-50%);
        }
        
        .mv-threshold-marker::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 4px solid #3b82f6;
        }

        .mv-progress-text {
          min-width: 45px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .mv-text-above {
          color: #10b981;
        }
        
        .mv-text-below {
          color: #64748b;
        }

        .mv-cv-generate-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin: 2rem auto 1rem;
          padding: 0.875rem 1.5rem;
          background: #007BFF;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
          min-width: 200px;
        }

        .mv-cv-generate-button:not(:disabled):hover {
          background: #0056b3;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0, 123, 255, 0.15);
        }

        .mv-cv-generate-button:not(:disabled):active {
          transform: translateY(0);
        }

        .mv-cv-generate-button.mv-generating {
          background: #e2e8f0;
          color: #64748b;
          cursor: default;
          transform: none;
          box-shadow: none;
        }

        .mv-button-icon {
          font-size: 1rem;
        }

        .mv-button-icon.mv-spinning {
          animation: mv-spin 1s linear infinite;
        }

        .mv-loading-container {
          margin: 0 auto;
          width: 100%;
          max-width: 300px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .mv-loading-bar-wrapper {
          width: 100%;
          height: 4px;
          background: #e2e8f0;
          border-radius: 2px;
          overflow: hidden;
        }

        .mv-loading-bar {
          height: 100%;
          background: linear-gradient(
            90deg,
            #007BFF 0%,
            #0056b3 50%,
            #007BFF 100%
          );
          background-size: 200% 100%;
          border-radius: 2px;
          transition: width 0.2s ease;
          animation: mv-gradient 2s linear infinite;
        }

        .mv-loading-text {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }

        @keyframes mv-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes mv-gradient {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
        
        /* Responsive styles */
        @media (max-width: 768px) {
          .mv-middle-view {
            padding: 1.5rem;
          }
          
          .mv-progress-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
            padding: 1rem;
          }
          
          .mv-label-section {
            width: 100%;
            margin-bottom: 0.5rem;
          }
          
          .mv-threshold-line-container {
            flex-direction: column-reverse;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .mv-threshold-line {
            width: 100%;
            height: 1px;
          }
          
          .mv-threshold-tooltip {
            position: relative;
            margin-top: 0.5rem;
          }
        }

        .mv-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 20px;
          border-radius: 8px;
          background: white;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          animation: slideIn 0.3s ease forwards;
        }

        .mv-notification.success {
          border-left: 4px solid #10b981;
          background: #f0fdf4;
          color: #166534;
        }

        .mv-notification.error {
          border-left: 4px solid #ef4444;
          background: #fef2f2;
          color: #991b1b;
        }

        .mv-notification.info {
          border-left: 4px solid #3b82f6;
          background: #eff6ff;
          color: #1e40af;
        }

        .mv-notification-close {
          background: transparent;
          border: none;
          cursor: pointer;
          font-size: 18px;
          padding: 0;
          color: currentColor;
          opacity: 0.7;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mv-notification-close:hover {
          opacity: 1;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};



export default MiddleView;
